<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import type { VoterSummary } from '../lib/stores/voters';
  import { currentUser } from '../lib/stores/auth';
  import { setVoterTargeting, apiFetchAuthed, getVoter } from '../lib/api';
  import { toast } from '$lib/toast';
  import { targetingSourceLabel, isEffectivelyOnList } from '../lib/voterTargeting';
  import { outreachLabel } from '../lib/outreach';
  import { ballotBadge } from '../lib/ballot';
  import RecordContact from './RecordContact.svelte';
  import VoterEdit from './VoterEdit.svelte';
  import VoterHistory from './VoterHistory.svelte';

  export let voter: VoterSummary;

  const dispatch = createEventDispatcher<{ close: void; contactSaved: VoterSummary; voterUpdated: VoterSummary }>();

  let showRecordContact = false;
  let showEdit = false;
  let showHistory = false;
  let togglingTarget = false;
  let savingNotHome = false;

  $: targetingState = {
    on_target_list: voter.on_target_list ?? false,
    manually_targeted: voter.manually_targeted ?? false,
    manually_excluded: voter.manually_excluded ?? false,
    newly_registered: voter.newly_registered ?? false,
    petition_signed: voter.petition_signed ?? false,
  };
  $: ballot = ballotBadge(voter);
  $: effectiveOnList = isEffectivelyOnList(targetingState);
  $: sourceLabel = targetingSourceLabel(targetingState);

  async function onTargetingToggle() {
    togglingTarget = true;
    try {
      const updated = await setVoterTargeting(voter.id, !effectiveOnList);
      voter = { ...voter, ...updated };
    } catch (e) {
      console.error('failed to update targeting', e);
      toast('Could not update targeting, try again', 'error');
    } finally {
      togglingTarget = false;
    }
  }

  const PARTY_LABELS: Record<string, string> = {
    DEM: 'Democrat',
    REP: 'Republican',
    UNA: 'Unaffiliated',
    LIB: 'Libertarian',
    GRE: 'Green',
    CST: 'Constitution',
    NLB: 'No Labels',
  };
  function partyLabel(p: string | null): string {
    if (!p) return 'Not registered';
    return PARTY_LABELS[p] ?? p;
  }

  function mapsUrl(v: VoterSummary) {
    const q = encodeURIComponent(`${v.address}, ${v.city}, NC ${v.zip}`);
    return `https://maps.google.com/?q=${q}`;
  }

  function contactSummary(v: VoterSummary): string {
    if (!v.latest_contact_result) return 'Never contacted';
    const d = v.latest_contact_at ? new Date(v.latest_contact_at).toLocaleDateString() : '';
    if (v.latest_contact_result === 'spoke') {
      return `Spoke ${d ? `on ${d}` : ''} — support ${v.latest_support_level}/5`;
    }
    const labels: Record<string, string> = {
      not_home: 'Not home', refused: 'Refused', moved: 'Moved', wrong_address: 'Wrong address',
    };
    return `${labels[v.latest_contact_result] ?? v.latest_contact_result} ${d ? `on ${d}` : ''}`;
  }

  function onContactSaved(e: CustomEvent<VoterSummary>) {
    showRecordContact = false;
    dispatch('contactSaved', e.detail);
  }

  // One-tap "Not home" — the most common door-knock outcome. Skips the full
  // record-contact modal entirely.
  async function recordNotHome() {
    if (savingNotHome || voter.do_not_contact) return;
    savingNotHome = true;
    try {
      const res = await apiFetchAuthed('/api/contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voter_id: voter.id,
          contact_method: 'door',
          contact_result: 'not_home',
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail ?? `HTTP ${res.status}`);
      }
      // Mirror the server's sticky-spoke rule, as RecordContact does: a
      // not_home does not replace an earlier spoke as the latest result.
      const keepSpoke = voter.latest_contact_result === 'spoke';
      const updated: VoterSummary = keepSpoke ? { ...voter } : {
        ...voter,
        latest_contact_result: 'not_home',
        latest_support_level: null,
        latest_contact_at: new Date().toISOString(),
      };
      voter = updated;
      toast('Not home recorded', 'success');
      dispatch('contactSaved', updated);
    } catch (e) {
      toast('Could not record Not home, try again', 'error');
      console.error('failed to record not_home', e);
    } finally {
      savingNotHome = false;
    }
  }
</script>

<!-- Backdrop -->
<button class="backdrop" on:click={() => dispatch('close')} aria-label="Close voter detail" />

<section class="sheet" role="dialog" aria-modal="true" aria-label="Voter detail">
  <button class="close-btn" on:click={() => dispatch('close')} aria-label="Close">✕</button>

  <h2 class="name">
    {voter.first_name} {voter.last_name}
    {#if voter.newly_registered}
      <span class="new-tag">NEW</span>
    {/if}
  </h2>

  <p class="affiliation party-{(voter.party ?? 'none').toLowerCase()}">
    <span class="aff-dot" aria-hidden="true"></span>
    <span class="aff-label">{partyLabel(voter.party)}</span>
  </p>

  <div class="meta">
    <a href={mapsUrl(voter)} target="_blank" rel="noopener" class="address">
      {voter.address}, {voter.city} {voter.zip}
    </a>
    {#if voter.age}
      <span class="badge">Age {voter.age}</span>
    {/if}
    {#if voter.county}
      <span class="badge">{voter.county} County</span>
    {/if}
    {#if voter.precinct}
      <span class="badge">Precinct {voter.precinct}</span>
    {/if}
    {#if voter.has_donated}
      <span class="badge outcome-badge">Donated</span>
    {/if}
    {#if voter.has_volunteered}
      <span class="badge outcome-badge">Signed up to volunteer</span>
    {/if}
    {#if voter.petition_signed}
      <span class="badge petition-badge" aria-label="Signed petition">✍ Signed petition</span>
    {/if}
    {#if ballot}
      <span class="badge ballot-badge ballot-{ballot.kind}" data-testid="ballot-badge">{ballot.label}</span>
    {/if}
  </div>

  {#if voter.phone}
    <a href="tel:{voter.phone}" class="phone-link">📞 {voter.phone}</a>
  {/if}

  {#if voter.email}
    <a href="mailto:{voter.email}" class="email-link">✉️ {voter.email}</a>
  {/if}

  <p class="contact-summary">{contactSummary(voter)}</p>

  <p class="text-outreach">Text outreach: {outreachLabel(voter.text_outreach)}</p>

  {#if voter.do_not_contact}
    <p class="dnc-warning" role="alert">⛔ Do not contact</p>
  {/if}

  <div class="actions">
    <button
      class="btn btn-primary"
      on:click={() => (showRecordContact = true)}
      disabled={voter.do_not_contact}
    >Record contact</button>
    {#if !voter.do_not_contact}
      <button
        class="btn btn-quick"
        on:click={recordNotHome}
        disabled={savingNotHome}
        title="One-tap: record a door knock with no answer"
      >{savingNotHome ? 'Saving…' : 'Not home ✓'}</button>
    {/if}
    <button class="btn btn-secondary" on:click={() => (showEdit = true)}>Edit voter</button>
    <button class="btn btn-secondary" on:click={() => (showHistory = true)}>View history</button>
  </div>

  {#if $currentUser?.role === 'admin'}
    <section class="targeting">
      <h3>Targeting</h3>
      <label class="switch">
        <input
          type="checkbox"
          checked={effectiveOnList}
          on:change={onTargetingToggle}
          disabled={togglingTarget}
        />
        <span>Show on volunteer map</span>
      </label>
      <p class="source">{sourceLabel}</p>
    </section>
  {/if}
</section>

{#if showRecordContact}
  <RecordContact
    {voter}
    on:close={() => (showRecordContact = false)}
    on:saved={onContactSaved}
  />
{/if}

{#if showEdit}
  <VoterEdit
    {voter}
    on:saved={(e) => {
      voter = { ...voter, ...e.detail };
      showEdit = false;
      // A DNC change repaints the map pin; the detail card stays open.
      dispatch('voterUpdated', voter);
    }}
    on:cancel={() => (showEdit = false)}
  />
{/if}

{#if showHistory}
  <VoterHistory
    {voter}
    on:close={() => (showHistory = false)}
    on:deleted={async () => {
      // A contact was deleted — re-fetch the voter so latest_* (and the
      // map pin color) reflect the new most-recent contact (or null).
      try {
        const updated = await getVoter(voter.id);
        voter = { ...voter, ...updated };
        dispatch('contactSaved', voter);  // re-uses parent's pin refresh path
      } catch (e) {
        console.error('Failed to refresh voter after delete', e);
      }
    }}
  />
{/if}

<style>
  .backdrop {
    position: fixed; inset: 0; background: rgba(0,0,0,.3);
    z-index: 900; border: none; cursor: default;
  }
  .sheet {
    position: fixed; bottom: 0; left: 0; right: 0;
    background: white; border-radius: 1rem 1rem 0 0;
    padding: 1.5rem 1rem;
    padding-bottom: calc(2rem + env(safe-area-inset-bottom));
    z-index: 901;
    max-height: 50dvh; overflow-y: auto;
    box-shadow: 0 -4px 20px rgba(0,0,0,.15);
  }
  .close-btn {
    position: absolute; top: 1rem; right: 1rem;
    background: none; border: none; font-size: 1.2rem;
    cursor: pointer; line-height: 1; padding: 4px 8px;
    min-width: 44px; min-height: 44px;
  }
  .name {
    margin: 0;
    font-family: var(--font-display, 'Fraunces', Georgia, serif);
    font-size: 1.45rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.1;
  }
  .new-tag {
    display: inline-block;
    vertical-align: middle;
    font-family: var(--font-body, system-ui, sans-serif);
    font-size: 0.65rem;
    background: var(--sun-soft, #FFF4BF);
    color: var(--sun-ink, #8A6B00);
    border: 1px solid var(--sun-bright, #E5B240);
    padding: 1px 6px;
    border-radius: 999px;
    font-weight: 700;
    letter-spacing: 0.06em;
    margin-left: 6px;
  }
  /* Affiliation — its own labeled line, color-coded by party */
  .affiliation {
    margin: 4px 0 0.85rem;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.95rem;
    font-weight: 500;
    color: var(--ink, #15211B);
  }
  /* Standard US party colors. Dem = a true bright blue (not navy);
     Rep = primary red; Lib = the yellow on their porcupine flag;
     Green = a clean leafy green (distinct from the campaign's deeper
     forest); Unaffiliated = a mid gray with a paler halo so it reads
     softly against the white card. */
  .aff-dot {
    width: 11px; height: 11px;
    border-radius: 50%;
    background: #9CA3AF;
    box-shadow: 0 0 0 3px rgba(156, 163, 175, 0.18);
    border: 1px solid rgba(0, 0, 0, 0.08);
  }
  .party-dem .aff-dot  { background: #1B4FE0; box-shadow: 0 0 0 3px rgba(27, 79, 224, 0.18); border-color: rgba(27, 79, 224, 0.4); }
  .party-rep .aff-dot  { background: #D62E2E; box-shadow: 0 0 0 3px rgba(214, 46, 46, 0.18); border-color: rgba(214, 46, 46, 0.4); }
  .party-lib .aff-dot  { background: #FFCC00; box-shadow: 0 0 0 3px rgba(255, 204, 0, 0.30);  border-color: rgba(180, 130, 0, 0.55); }
  .party-gre .aff-dot  { background: #16A765; box-shadow: 0 0 0 3px rgba(22, 167, 101, 0.18); border-color: rgba(22, 167, 101, 0.4); }
  .party-cst .aff-dot  { background: #8B1538; box-shadow: 0 0 0 3px rgba(139, 21, 56, 0.18);  border-color: rgba(139, 21, 56, 0.4); }
  .party-una .aff-dot  { background: #D1D5DB; box-shadow: 0 0 0 3px rgba(209, 213, 219, 0.45); border-color: rgba(0, 0, 0, 0.18); }
  .party-none .aff-dot { background: #FFFFFF; box-shadow: 0 0 0 3px rgba(209, 213, 219, 0.55); border-color: rgba(0, 0, 0, 0.22); }
  .aff-label {
    font-family: var(--font-display, 'Fraunces', Georgia, serif);
    font-style: italic;
  }

  .meta { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-bottom: 0.75rem; }
  .address { font-size: 0.9rem; color: #1a73e8; text-decoration: none; flex-basis: 100%; }
  .badge {
    font-size: 0.75rem; padding: 2px 8px; border-radius: 999px;
    background: #eee; color: #333;
  }
  .petition-badge {
    background: var(--leaf, #2e7d32);
    color: var(--paper, #fff);
  }
  .ballot-voted { background: #1e40af; color: #fff; }
  .ballot-pending { background: #fde68a; color: #1a1a1a; }
  .outcome-badge {
    background: #8e6bb8; /* matches the map's supporter-action violet */
    color: #fff;
  }
  .phone-link { display: block; margin-bottom: 0.5rem; color: #1a73e8; font-size: 0.95rem; }
  .email-link { display: block; margin-bottom: 0.75rem; color: #1a73e8; font-size: 0.95rem; word-break: break-all; }
  .contact-summary { font-size: 0.9rem; color: #555; margin-bottom: 1rem; }
  .text-outreach { font-size: 0.85rem; color: #555; margin: -0.5rem 0 1rem; }
  .dnc-warning { color: #dc2626; font-weight: 600; font-size: 0.9rem; margin-bottom: 1rem; }
  .actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }
  .btn {
    flex: 1; min-width: 120px; padding: 0.6rem 1rem;
    border-radius: 8px; border: none; cursor: pointer;
    font-size: 0.95rem; font-weight: 600; min-height: 44px;
  }
  .btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .btn-primary { background: #1a73e8; color: white; }
  .btn-primary:hover:not(:disabled) { background: #1557b0; }
  .btn-secondary { background: #eee; color: #333; }
  .btn-secondary:hover:not(:disabled) { background: #ddd; }
  .btn-quick { background: var(--leaf, #1f5135); color: var(--paper, #fff); }
  .btn-quick:hover:not(:disabled) { background: var(--leaf-bright, #2d7a4f); }

  .targeting {
    border-top: 1px solid var(--rule);
    margin-top: 1rem;
    padding-top: 0.75rem;
  }
  .targeting h3 {
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 600;
    color: var(--ink-soft);
    margin: 0 0 0.5rem;
  }
  .switch { display: inline-flex; align-items: center; gap: 0.5rem; cursor: pointer; }
  .switch input { width: 1.1rem; height: 1.1rem; }
  .source { font-size: 0.78rem; color: var(--ink-soft); margin: 0.25rem 0 0; }
</style>
